// Dip — <dip-bundle-save>: per-product volume + bundling bonus (sections/dip-bundle-save.liquid).
//
// v3 (Charlie 2026-08-25): product-led. Four toggleable products, each with its own volume
// ladder priced individually; ONE percentage bonus for the basket:
//   bonus % = depth (+5% when EVERY selected product is x2 its base rung, +10% at x3;
//   multi-surface participates fully, its base rung being the 6-pack = 24 bottles at 2
//   bottles a month) + breadth (+5% flat for 2+ products). Totals 5 / 10 / 15.
//
// PRICE INTEGRITY. A percent is only ever SHOWN when a Basic percentage code for exactly
// that percent exists in `data-bundle-codes` (keyed by the number, restricted to these four
// products). No code, no promise: the panel quotes the undiscounted total instead. The code
// is applied on add through the supported cart update API (dip-msc-guard.js precedent).
//
// Cart mechanics mirror dip-buy-box.js / dip-home.js. The welcome kit LEFT this page on
// 2026-09-04 (offer decision ruling 1, build phase 5). What survives is the multi-surface
// hardware (rulings 3 + 9): an MSC rung brings 2 bottles + 2 cloths, zeroed by the
// FREE-KIT-MSC-3/-6 automatics, through the shared assets/dip-hardware.js; paid extra sets
// (Charlie 2026-08-25) ride the stepper at the live price only while NO multi-surface rung is in
// the basket, because free and paid units on the same lines trip the BXGY allocation trap
// (docs/dip-bundle-save.md gate 5.7). Every line states its own renewal price and cadence
// (ruling 15) with delivery on the order total (one parcel, one cadence). A rung whose lines
// resolve to no live variant (the 180-wash rungs until D1's variants exist) is hidden, and the
// default falls to the first rung that does resolve.
//
// NO EM DASHES in rendered strings.

import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { tagCart } from '@theme/dip-arm';
// The roll-on grammar and the delivery figures (assets/dip-offer.js): the first-order offer
// itself never applies to these stock-up sizes, only its line templates and shipping rule.
import { renewalShipMinor } from '@theme/dip-offer';
// The multi-surface hardware (assets/dip-hardware.js): the same helper as every other surface.
import { hwActive, hwLines, hwIncludedText, hwWorth, reconcileHardwareLines, publishHardwareCodes } from '@theme/dip-hardware';

// Active presentment currency + locale. Liquid already converted every price in the island,
// so JS only formats. MINOR is always 100: Shopify stores amount x 100 in every currency.
const CURRENCY = window.Shopify?.currency?.active || 'GBP';
const LOCALE = document.documentElement.lang || 'en';
const MINOR = 100;
const _fmt = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: CURRENCY });
const money = (minorUnits) => _fmt.format((minorUnits || 0) / MINOR);
const _fmt0 = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: CURRENCY, minimumFractionDigits: 0, maximumFractionDigits: 0 });
const moneyTidy = (minorUnits) => ((minorUnits || 0) % MINOR === 0 ? _fmt0 : _fmt).format((minorUnits || 0) / MINOR);
// Per-wash rates round to whole pennies (Charlie 2026-08-26: nobody has point-something
// of a penny), so the standard two-decimal money formatter does the job.
const perWash = (minorUnits) => _fmt.format((minorUnits || 0) / MINOR);

const ALL_PRODUCTS = ['laundry', 'dishwasher', 'fabcon', 'msc'];

/**
 * Parse a volume option's line string into resolvable specs.
 * "120 washes x1, 60 washes x1" -> [{ size, qty }]. Sizes match case-insensitively
 * because the option values disagree on capitalisation across products.
 */
const parseLines = (spec) => String(spec || '')
  .split(',')
  .map((part) => part.trim())
  .filter(Boolean)
  .map((part) => {
    const m = part.match(/^(.*?)\s*[x×]\s*(\d+)$/i);
    return m ? { size: m[1].trim(), qty: Math.max(1, Number(m[2]) || 1) } : { size: part, qty: 1 };
  });

/**
 * Read the basket's item count out of rendered section HTML.
 * Duplicated byte-identically in dip-buy-box.js, dip-home.js and dip-cart.js: keep in sync.
 */
const dipCartCountFromSections = (sections) => {
  if (!sections) return null;
  for (const html of Object.values(sections)) {
    if (typeof html !== 'string') continue;
    const match = html.match(/data-testid="cart-bubble"[^>]*>\s*([\d,]+)/);
    if (match) return parseInt(match[1].replace(/,/g, ''), 10);
  }
  return null;
};

class DipBundleSave extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-dip-data]');
    if (!dataEl) return;
    try {
      this.model = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('[dip-bundle-save] bad data island', e);
      return;
    }

    this.copy = this.model.copy || {};
    this.options = this.model.options || {};
    this.products = ALL_PRODUCTS.filter((p) => this.model[p] && (this.options[p] || []).length);
    if (!this.products.length) return;

    this.CHECK = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 13l4 4L19 7"/></svg>';
    this.scentColours = this.model.scentColours || {};

    this.refs = {};
    this.querySelectorAll('[ref]').forEach((el) => {
      const k = el.getAttribute('ref');
      if (!(k in this.refs)) this.refs[k] = el;
    });

    // Percent -> discount code. A percent absent from this map cannot be shown or promised.
    try { this.codes = JSON.parse(this.dataset.bundleCodes || '{}') || {}; } catch { this.codes = {}; }
    this.depthPcts = (this.model.depthPcts || [0, 5, 10]).map(Number);
    this.breadthPct = Number(this.model.breadthPct) || 0;

    const defaultOn = (this.model.defaultOn || []).map((x) => String(x).trim());
    this.on = {};
    this.vol = {};
    this.products.forEach((p) => {
      this.on[p] = defaultOn.includes(p);
      const di = (this.options[p] || []).findIndex((o) => o.isDefault);
      this.vol[p] = di >= 0 ? di : 0;
    });
    // `wantSub` is what the customer ASKED for; `sub` is what the selected basket supports.
    this.wantSub = true;
    this.sub = true;
    this.months = 0;
    this.freqTouched = false;
    this.scent = this.model.laundry?.scents?.[0] || '';
    // Extra hardware sets (bottle + cloth), PAID at the live price, up to hwCap, and only while no
    // multi-surface rung is in (its free set would collide with paid units on the same lines).
    this.hw = 0;
    this.hardware = this.model.hardware || null;
    this.offer = this.model.offer || { active: false };

    // Deep-linkable state: ?bundle=laundry.2,dishwasher.2&plan=once selects those
    // products at those volume indexes (everything else off) and can flip the purchase
    // type. How design previews are captured, and ready-made for campaign links to a
    // preconfigured bundle. Bad params fall through to the defaults.
    try {
      const q = new URLSearchParams(window.location.search);
      const spec = q.get('bundle');
      if (spec) {
        this.products.forEach((p) => { this.on[p] = false; });
        spec.split(',').forEach((part) => {
          const [prod, idx] = part.trim().split('.');
          if (this.products.includes(prod)) {
            this.on[prod] = true;
            const i = Number(idx);
            if (Number.isInteger(i) && (this.options[prod] || [])[i]) this.vol[prod] = i;
          }
        });
      }
      if (q.get('plan') === 'once') this.wantSub = false;
    } catch { /* bad params: defaults stand */ }

    this.#bind();
    this.render();
    // The guard's hardware quantity model (rulings 3 + 9): this page posts MSC packs too.
    publishHardwareCodes('bundle-save', this.hardware, this.model.msc?.variants || []);

    // Stuck-state detection for the floating savings bar: while the sentinel (the bar's
    // resting position) is scrolled behind the sticky offset, the bar is pinned and CSS
    // compacts it on phones (~23% of the viewport otherwise). A rAF-throttled scroll
    // check, not IntersectionObserver, which proved unreliable under emulated viewports.
    if (this.refs.bonusSentinel && this.refs.bonusBar) {
      let ticking = false;
      const check = () => {
        ticking = false;
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 64;
        const stuck = this.refs.bonusSentinel.getBoundingClientRect().top < header + 11;
        this.refs.bonusBar.classList.toggle('is-stuck', stuck);
      };
      const onScroll = () => {
        if (!ticking) { ticking = true; requestAnimationFrame(check); }
      };
      // Capture-phase on document catches nested scroll containers too (scroll does not
      // bubble); window + visualViewport are the canonical dip-sticky-bar listener set.
      document.addEventListener('scroll', onScroll, { passive: true, capture: true });
      window.addEventListener('scroll', onScroll, { passive: true });
      if (window.visualViewport) {
        window.visualViewport.addEventListener('scroll', onScroll);
        window.visualViewport.addEventListener('resize', onScroll);
      }
      window.addEventListener('resize', onScroll);
      this.checkBonusStuck = check;
      check();
    }
  }

  // ---- Selection ---------------------------------------------------------------
  #selected() { return this.products.filter((p) => this.on[p]); }
  #optionOf(p) { return (this.options[p] || [])[this.vol[p]] || null; }
  /** Does this option resolve to live variants at all? (The 180-wash rungs do not until D1's
   *  variants exist; a rung that resolves to nothing is hidden, never sold empty.) */
  #resolvable(p, o) { return !!o && this.#resolve(p, o.lines).length > 0; }
  /** Keep every product's selected rung on one that resolves. */
  #settleRungs() {
    this.products.forEach((p) => {
      const opts = this.options[p] || [];
      if (this.#resolvable(p, opts[this.vol[p]])) return;
      const i = opts.findIndex((o) => this.#resolvable(p, o));
      if (i >= 0) this.vol[p] = i;
    });
  }

  /** Resolve one product's option lines to real variants at the current scent. A size
   * matching no available variant is DROPPED, never substituted. */
  #resolve(p, spec) {
    const group = this.model[p];
    if (!group) return [];
    const list = group.variants || [];
    const wantScent = p === 'laundry' && this.scent;
    return parseLines(spec).map(({ size, qty }) => {
      const norm = size.toLowerCase();
      const v = list.find((x) => String(x.size || '').toLowerCase() === norm
        && (!wantScent || x.scent === this.scent) && x.available);
      return v ? { key: p, variant: v, qty } : null;
    }).filter(Boolean);
  }

  /** All lines of the current basket (or of one product's given option index). */
  #basketLines() {
    return this.#selected().flatMap((p) => {
      const o = this.#optionOf(p);
      return o ? this.#resolve(p, o.lines) : [];
    });
  }

  // ---- Coverage & cadence --------------------------------------------------------
  /** How long one product's option lasts at the assumed usage, in months. */
  #coverageOf(p, opt) {
    const o = opt || this.#optionOf(p);
    if (!o) return 0;
    const lines = this.#resolve(p, o.lines);
    if (p === 'msc') {
      const packs = lines.reduce((s, l) => s + (l.variant.packs || 0) * l.qty, 0);
      const bpm = Number(this.model.bottlesPerMonth) || 1;
      return packs > 0 ? Math.round(packs * (Number(this.model.bottlesPerPack) || 4) / bpm) : 0;
    }
    const washes = lines.reduce((s, l) => s + (l.variant.washes || 0) * l.qty, 0);
    const wpm = Number(this.model.washesPerMonth) || 30;
    return washes > 0 ? Math.round(washes / wpm) : 0;
  }

  /** Basket default cadence: the SHORTEST coverage among selected lines, capped at 12
   * months (Charlie: larger bundles default to yearly). */
  #wantedCadence() {
    const covs = this.#selected().map((p) => this.#coverageOf(p)).filter(Boolean);
    if (!covs.length) return 0;
    return Math.min(12, Math.min(...covs));
  }

  /** Cadences with a real selling plan on EVERY line, never shorter than the default.
   * No downward fallback: a bundle on a shorter plan ships and bills again early. */
  #availableCadences() {
    const authored = (this.model.cadenceMonths || []).map(Number).filter(Boolean);
    const lines = this.#basketLines();
    const wanted = this.#wantedCadence();
    if (!lines.length || !wanted) return [];
    return authored.filter((m) => m >= wanted
      && lines.every((l) => l.variant.plans && l.variant.plans[m]));
  }

  #defaultCadence() {
    const want = this.#wantedCadence();
    return this.#availableCadences().includes(want) ? want : 0;
  }

  #cadenceWord(m) {
    if (!m) return '';
    if (m === 1) return 'every month';
    if (m === 12) return 'once a year';
    return `every ${m} months`;
  }

  #coverWord(mo) {
    if (!mo) return '';
    if (mo === 12) return 'about a year';
    if (mo === 24) return 'about two years';
    if (mo === 36) return 'about three years';
    return `about ${mo} months`;
  }

  // ---- The pack-by-pack comparator ---------------------------------------------------
  /**
   * What the SAME basket costs bought as each product's smallest pack, on the same plan.
   * The sales layer's anchor (Charlie 2026-08-26): the page argues per-wash, percentage
   * and money against buying things individually, and the smallest pack is that route.
   * A rate comparison (smallest pack price per wash x the basket's washes), so it needs
   * no invented quantities; hardware and the kit are excluded on both sides.
   */
  #packByPack(sub, months) {
    let cost = 0, coreCost = 0, washes = 0;
    const perProduct = [];
    this.#selected().forEach((p) => {
      const o = this.#optionOf(p);
      if (!o) return;
      const lines = this.#resolve(p, o.lines);
      const list = (this.model[p]?.variants || []).filter((v) => v.available
        && (p !== 'laundry' || !this.scent || v.scent === this.scent));
      const unit = (v) => (p === 'msc' ? v.packs || 0 : v.washes || 0);
      const smallest = list.reduce((a, b) => (a && unit(a) <= unit(b) ? a : b), null);
      if (!smallest || !unit(smallest)) return;
      const planFirst = smallest.plans && Object.values(smallest.plans)[0];
      const price = sub && planFirst ? planFirst.raw : smallest.otRaw || 0;
      const rate = price / unit(smallest);
      const qty = lines.reduce((s, l) => s + unit(l.variant) * l.qty, 0);
      const gross = lines.reduce((s, l) => {
        const v = l.variant;
        const raw = sub && v.plans && v.plans[months] ? v.plans[months].raw : v.otRaw;
        return s + (raw || 0) * l.qty;
      }, 0);
      perProduct.push({ p, small: rate * qty, gross });
      cost += rate * qty;
      if (p !== 'msc') { coreCost += rate * qty; washes += qty; }
    });
    return { cost, coreCost, washes, perProduct };
  }

  // ---- The bonus rule --------------------------------------------------------------
  /** Depth tier = the LOWEST tier among ALL selected products, MSC included (Charlie
   * 2026-08-25: the same logic as the other products), so "double everything" is literal. */
  #depthTier() {
    const tiers = this.#selected().map((p) => Number(this.#optionOf(p)?.depth) || 1);
    return tiers.length ? Math.min(...tiers) : 0;
  }

  /** The basket's entitled percent, honoured only when its code exists. */
  #bonus() {
    const depthTier = this.#depthTier();
    const depth = this.depthPcts[Math.max(0, depthTier - 1)] || 0;
    const breadth = this.#selected().length >= 2 ? this.breadthPct : 0;
    const wanted = depth + breadth;
    const code = wanted > 0 ? this.codes[String(wanted)] : '';
    const ok = typeof code === 'string' && code.trim() !== '';
    return { depth, breadth, depthTier, wanted, pct: ok ? wanted : 0, code: ok ? code.trim() : '' };
  }

  // ---- Pricing ---------------------------------------------------------------------
  /** Totals for the basket (or one product's option in isolation, for menu cards). */
  #totals(sub, months, onlyLines) {
    const lines = onlyLines || this.#basketLines();
    let gross = 0, once = 0, washes = 0;
    lines.forEach(({ variant, qty }) => {
      once += (variant.otRaw || 0) * qty;
      const planRaw = sub && variant.plans && variant.plans[months] ? variant.plans[months].raw : variant.otRaw;
      gross += (planRaw || 0) * qty;
      washes += (variant.washes || 0) * qty;
    });
    const bonus = this.#bonus();
    const pct = onlyLines ? 0 : bonus.pct;  // menu cards are always undiscounted
    const net = Math.round(gross * (1 - pct / 100));
    // Paid hardware extras ride OUTSIDE the percentage: the bundle codes are restricted to
    // the four sheet products, so hardware is never discounted and the display must match.
    let hwRaw = 0;
    if (!onlyLines && this.hw > 0) {
      const h = this.hardware || {};
      hwRaw = this.hw * ((Number(h.bottlePrice) || 0) + (Number(h.clothPrice) || 0));
    }
    return { lines, gross: gross + hwRaw, once: once + hwRaw, net: net + hwRaw,
      washes, pct, bonus, bundleSaving: gross - net, hwRaw };
  }

  /** The multi-surface rung's free hardware (rulings 3 + 9): the size of its first line drives
   *  the per-size quantities (2 + 2 on the 3- and 6-packs), whatever the rung's quantity, and the
   *  lines post as zero-priced included listings (ruling-11 fix b). Null when no MSC rung is in. */
  #mscHardware() {
    if (!this.on.msc || !hwActive(this.hardware)) return null;
    const o = this.#optionOf('msc');
    const lines = o ? this.#resolve('msc', o.lines) : [];
    const size = lines[0]?.variant?.size;
    if (!size) return null;
    const items = hwLines(this.hardware, size);
    if (!items.length) return null;
    return { size, items, text: hwIncludedText(this.hardware, size), worth: hwWorth(this.hardware, size) };
  }

  // ---- Binding -----------------------------------------------------------------------
  #bind() {
    this.products.forEach((p) => {
      this.refs[`card-${p}`]?.addEventListener('click', () => {
        // An empty basket is allowed (Charlie 2026-08-26): the page falls into a clean
        // empty state rather than fighting the customer's last click.
        this.on[p] = !this.on[p];
        this.freqTouched = false;
        this.render();
      });
      this.querySelector(`[data-prod="${p}"]`)?.addEventListener('change', (e) => {
        const r = e.target.closest('input[type=radio]');
        if (!r || !r.checked) return;
        this.vol[p] = Number(r.value) || 0;
        this.freqTouched = false;
        this.render();
      });
    });
    this.querySelectorAll('input[name^="dip-bsave-type"]').forEach((r) => {
      r.addEventListener('change', () => {
        if (!r.checked) return;
        this.wantSub = r.value === 'sub';
        this.render();
      });
    });
    this.querySelectorAll('input[name^="dip-bsave-scent"]').forEach((r) => {
      r.addEventListener('change', () => { this.scent = r.value; this.render(); });
    });
    this.refs.freq?.addEventListener('change', () => {
      this.months = Number(this.refs.freq.value) || this.months;
      this.freqTouched = true;
      this.render();
    });
    this.refs.cta?.addEventListener('click', (e) => { e.preventDefault(); this.#addToCart(); });
    // Hardware-extras stepper (event delegation, builder parity). render() clamps to the cap.
    this.refs.recapList?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-step="hw"]');
      if (!btn) return;
      this.hw = Math.max(0, (this.hw || 0) + (Number(btn.dataset.dir) || 0));
      this.render();
    });
    // One-time in one tap (builder parity): switches the purchase type AND adds.
    this.refs.once?.addEventListener('click', (e) => {
      e.preventDefault();
      this.wantSub = false;
      this.render();
      this.#addToCart();
    });
  }

  // ---- Render ------------------------------------------------------------------------
  render() {
    if (this.adding) return;
    const C = this.copy;
    this.#settleRungs();
    const selected = this.#selected();
    const mscHw = this.#mscHardware();
    const avail = this.#availableCadences();
    const canSub = this.#defaultCadence() > 0;
    this.sub = this.wantSub && canSub;
    if (!this.sub) this.months = 0;
    else if (!avail.includes(this.months) || !this.freqTouched) this.months = this.#defaultCadence();
    // Paid extras: capped, OFF whenever a multi-surface rung is in (its free set and paid units
    // on the same lines were the BXGY allocation trap, doc gate 5.7), and OFF whenever the
    // hardware products carry no price (ruling-11 fix b: the pickers point at zero-priced
    // included listings, so a "paid" extra would post free; spares are sold on the MSC page).
    const hwCap = Number(this.model.hwCap) || 3;
    const hwSetRaw = (Number(this.hardware?.bottlePrice) || 0) + (Number(this.hardware?.clothPrice) || 0);
    const hwMax = (mscHw || hwSetRaw <= 0) ? 0 : hwCap;
    this.hw = Math.max(0, Math.min(this.hw || 0, hwMax));
    const T = this.#totals(this.sub, this.months);
    const bonus = T.bonus;
    // The pack-by-pack comparator feeds both the sales band (above the ladders) and the
    // savings stack (below), so it is computed once, here.
    const small = this.#packByPack(this.sub, this.months);
    const sheetsGross = T.gross - (T.hwRaw || 0);
    const sheetsNet = T.net - (T.hwRaw || 0);

    // ---- product cards + per-product volume groups
    this.products.forEach((p) => {
      const card = this.refs[`card-${p}`];
      if (card) {
        card.classList.toggle('is-selected', !!this.on[p]);
        card.setAttribute('aria-pressed', this.on[p] ? 'true' : 'false');
      }
      const wrap = this.refs[`vol-${p}`];
      if (wrap) wrap.hidden = !this.on[p];
      if (!this.on[p]) return;

      const grid = this.querySelector(`[data-prod="${p}"]`);
      if (!grid) return;
      grid.querySelectorAll('.dip-tier').forEach((tierCard, i) => {
        const o = (this.options[p] || [])[i];
        if (!o) return;
        const lines = this.#resolve(p, o.lines);
        // A rung with no live variant behind it is hidden rather than sold empty.
        tierCard.hidden = !lines.length;
        if (!lines.length) { tierCard.classList.remove('is-selected'); return; }
        const cov = this.#coverageOf(p, o);
        // Menu figures: this option alone, undiscounted, at the option's own cadence.
        // Both plan prices are computed regardless of the active purchase type, so the
        // card can always state the other one as a labelled alternative.
        const cad = Math.min(12, cov || 12);
        const hasPlan = lines.length > 0 && lines.every((l) => l.variant.plans && l.variant.plans[cad]);
        const canPlan = this.sub && hasPlan;
        const tt = this.#totals(canPlan, canPlan ? cad : 0, lines);
        const input = tierCard.querySelector('input');
        const isSel = i === this.vol[p];
        tierCard.classList.toggle('is-selected', isSel);
        if (input) input.checked = isSel;

        const permo = tierCard.querySelector('[data-permo]');
        if (permo) permo.textContent = tt.gross > 0 ? money(tt.gross) : '';
        // MSC cards say what the packs become; wash-product cards carry NO timeframe line
        // (Charlie 2026-08-26: volume buyers are not thinking in months). The coverage
        // maths still drives the cadence default and the subscribe row's wording.
        const sum = tierCard.querySelector('[data-tiersum]');
        if (sum) {
          if (p === 'msc') {
            const packs = lines.reduce((s2, l) => s2 + (l.variant.packs || 0) * l.qty, 0);
            sum.textContent = packs > 0
              ? (C.makesWord || 'Makes [n] bottles of cleaner').replace('[n]', packs * (Number(this.model.bottlesPerPack) || 4))
              : '';
          } else {
            sum.textContent = '';
          }
        }
        const pw = tierCard.querySelector('[data-perwash]');
        if (pw) {
          const show = tt.washes > 0 && tt.gross > 0;
          pw.hidden = !show;
          if (show) pw.textContent = `${perWash(tt.gross / tt.washes)} ${C.perWashNote || 'cost per wash'}`;
        }
      });
    });

    // ---- formula row rides the laundry selection (builder pattern): theme swatch
    // colours per formula and the tick inside the selected swatch, byte-for-byte the
    // treatment dip-home.js gives the same pills.
    this.refs.scentRow?.classList.toggle('is-on', !!this.on.laundry);
    this.querySelectorAll('input[name^="dip-bsave-scent"]').forEach((r) => {
      const label = r.closest('.dip-scent-pill');
      if (!label) return;
      const col = this.scentColours[r.value] || {};
      label.style.setProperty('--st', col.fill || 'var(--dip-accent)');
      label.style.setProperty('--stb', col.border || 'var(--dip-accent-ink)');
      const on = r.value === this.scent;
      r.checked = on;
      label.classList.toggle('is-selected', on);
      const sw = label.querySelector('.dip-scent-sw');
      if (sw) sw.innerHTML = on ? this.CHECK : '';
    });

    // ---- bonus strip: a CHECKLIST, not a numeric bar (Charlie 2026-08-26). The bundling
    // bonus is flat, so a bar could not move when a third product was added and read as
    // broken; instead the bundling row ticks at 2+ products and the volume row upgrades
    // as sizes double and triple. Every figure derives from the configured percentages.
    if (this.refs.bonusH) {
      // The money headline: what this basket saves against the same washes bought as the
      // smallest packs on the same plan. Present whenever a basket exists, because the
      // pack-size saving is real at every rung, bonus or no bonus.
      const bandSaving = Math.max(0, Math.round(small.cost - sheetsNet));
      // The percentage is the basket's BEST line ("up to"), never the blend: a blended
      // percentage DROPS when a narrower-gap product joins (dishwasher's smallest pack is
      // already a 60, so its gap is ~31% against laundry's 40%), which read as a demotion
      // for bundling MORE (Charlie 2026-08-26). The max is monotonic: adding products only
      // adds candidates, and the bonus only pushes every line's own saving up. The money
      // headline beside it stays the exact basket figure.
      const bandPct = small.perProduct.reduce((best, e) => {
        if (!(e.small > 0)) return best;
        const net = e.gross * (1 - (T.pct || 0) / 100);
        return Math.max(best, Math.round(((e.small - net) / e.small) * 100));
      }, 0);
      this.refs.bonusH.textContent = bandSaving > 0
        ? (C.bandH || 'Save [amt] vs buying pack by pack').replace('[amt]', money(bandSaving))
        : (C.bonusOffH || 'Build a bigger bundle, pay less');
      if (this.refs.bonusPw) {
        // Wash-product subtotal only on both sides: MSC has no washes, so folding its
        // price in would corrupt the rate the line is comparing.
        const washGross = T.lines.filter((l) => l.key !== 'msc').reduce((sum, l) => {
          const v = l.variant;
          const raw = this.sub && v.plans && v.plans[this.months] ? v.plans[this.months].raw : v.otRaw;
          return sum + (raw || 0) * l.qty;
        }, 0);
        const washNet = washGross * (1 - T.pct / 100);
        const show = small.washes > 0 && T.washes > 0 && small.coreCost > 0 && washNet > 0;
        this.refs.bonusPw.hidden = !show;
        if (show) {
          this.refs.bonusPw.textContent = (C.bandPw || '[now] a wash instead of [was]')
            .replace('[now]', perWash(washNet / T.washes))
            .replace('[was]', perWash(small.coreCost / small.washes));
        }
      }
      if (this.refs.bonusRows) {
        const TICK = '<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 13l4 4L19 7"/></svg>';
        const rowHtml = (on, label, amt) =>
          `<span class="dip-bsave__bonusrow${on ? ' is-on' : ''}"><i>${on ? TICK : ''}</i><span>${label}</span><b>+${amt}%</b></span>`;
        // Every rung stays visible (Charlie 2026-08-26: never hide the triple), and the
        // amounts are ADDITIVE: the triple row shows its increment over double, so the
        // ticked rows always sum to the exact bonus in play (5 + 5 + 5 = the 15% total).
        const d2 = this.depthPcts[1] || 0;
        const d3 = this.depthPcts[2] || 0;
        const rows = [
          rowHtml(bonus.breadth > 0, C.bonusRowProducts || 'Bundle 2 or more products', this.breadthPct),
          rowHtml(bonus.depthTier >= 2, C.bonusRowDouble || 'Double every size', d2),
          rowHtml(bonus.depthTier >= 3, C.bonusRowTriple || 'Triple every size', Math.max(0, d3 - d2)),
        ];
        this.refs.bonusRows.innerHTML = rows.join('');
      }
      if (this.refs.bonusPct) {
        this.refs.bonusPct.hidden = !(bandPct > 0);
        if (bandPct > 0) {
          this.refs.bonusPct.innerHTML = `<small>${C.bandUpWord || 'up to'}</small>${bandPct}%<small>${C.bandPctWord || 'saved'}</small>`;
        }
      }
    }

    // ---- purchase-type rows
    const subT = this.#totals(true, this.#defaultCadence());
    const onceT = this.#totals(false, 0);
    if (this.refs.subPrice) this.refs.subPrice.textContent = canSub && subT.net > 0 ? money(subT.net) : '';
    if (this.refs.oncePrice) this.refs.oncePrice.textContent = onceT.net > 0 ? money(onceT.net) : '';
    this.refs.typeSubWrap?.classList.toggle('is-selected', this.sub);
    this.refs.typeOnceWrap?.classList.toggle('is-selected', !this.sub);
    if (this.refs.typeSubWrap) {
      this.refs.typeSubWrap.classList.toggle('is-disabled', !canSub);
      const input = this.refs.typeSubWrap.querySelector('input');
      if (input) { input.disabled = !canSub; input.checked = this.sub; }
    }
    const onceInput = this.refs.typeOnceWrap?.querySelector('input');
    if (onceInput) onceInput.checked = !this.sub;

    if (this.refs.subSub) {
      const covs = selected.map((p) => this.#coverageOf(p)).filter(Boolean);
      const maxCov = covs.length ? Math.max(...covs) : 0;
      if (!selected.length) {
        this.refs.subSub.textContent = 'Repeats when you run out. Skip, pause or cancel any time.';
      } else if (!canSub) {
        this.refs.subSub.textContent = C.noPlanNote
          || 'Not available on repeat at this size yet. Buy it once, and we will remind you before it runs out.';
      } else if (this.months && maxCov > this.months) {
        // Any line outlasting the cadence: state the skip right where the cadence is chosen.
        this.refs.subSub.textContent = `Repeats ${this.#cadenceWord(this.months)}. Skip any delivery you don't need.`;
      } else {
        const cover = this.#coverWord(maxCov);
        this.refs.subSub.textContent = cover
          ? `Lasts ${cover}, then repeats. Skip, pause or cancel any time.`
          : 'Repeats when you run out. Skip, pause or cancel any time.';
      }
    }

    // ---- frequency
    if (this.refs.freqWrap) this.refs.freqWrap.hidden = !this.sub || avail.length < 2;
    if (this.refs.freq && this.sub) {
      const wanted = avail.map(String).join(',');
      if (this.refs.freq.dataset.built !== wanted) {
        this.refs.freq.innerHTML = avail
          .map((m) => `<option value="${m}">${this.#cadenceWord(m).replace(/^e/, 'E').replace(/^o/, 'O')}</option>`)
          .join('');
        this.refs.freq.dataset.built = wanted;
      }
      this.refs.freq.value = String(this.months);
    }

    // ---- recap
    if (this.refs.recapList) {
      const bottles = Number(this.model.bottlesPerPack) || 4;
      const row = (title, detail, aside = '') =>
        `<li><span class="dip-recap-tx"><span class="dip-recap-t">${this.CHECK}${title}</span>${detail ? `<span class="dip-recap-s">${detail}</span>` : ''}</span>${aside}</li>`;
      // Each product line states its own renewal price and cadence (ruling 15): the plan
      // price of its lines at the basket cadence, renewing at standard price; on one-time,
      // nothing repeats. Delivery is judged on the ORDER (one parcel, one cadence) and
      // stated once, on the price block.
      const lineRollOn = (lines) => {
        if (!(this.sub && this.months)) return '';
        const raw = lines.reduce((s2, l) => {
          const v = l.variant;
          return s2 + ((v.plans && v.plans[this.months] ? v.plans[this.months].raw : 0) || 0) * l.qty;
        }, 0);
        return raw > 0 ? `${T.pct > 0 ? 'then ' : ''}${money(raw)} ${this.#cadenceWord(this.months)}` : '';
      };
      const rows = selected.map((p) => {
        const o = this.#optionOf(p);
        const lines = o ? this.#resolve(p, o.lines) : [];
        const name = this.model[p]?.title || p;
        const roll = lineRollOn(lines);
        if (p === 'msc') {
          const packs = lines.reduce((s, l) => s + (l.variant.packs || 0) * l.qty, 0);
          const makes = (C.makesWord || 'Makes [n] bottles of cleaner')
            .replace('[n]', packs * bottles).replace(/^\w/, (ch) => ch.toLowerCase());
          let out = row(name, [`${packs} pack${packs === 1 ? '' : 's'} · ${makes}`, roll].filter(Boolean).join(' · '));
          // The rung's free hardware (rulings 3 + 9), zeroed by the FREE-KIT-MSC automatics.
          if (mscHw?.text) {
            out += row(mscHw.text, '', `<span class="dip-kit-free"><s>${moneyTidy(mscHw.worth)}</s> ${C.freeWord || 'FREE'}</span>`);
          }
          return out;
        }
        const washes = lines.reduce((s, l) => s + (l.variant.washes || 0) * l.qty, 0);
        const packs = lines.map((l) => (l.qty > 1 ? `${l.qty} × ${l.variant.size}` : l.variant.size));
        const breakdown = packs.length > 1 || /×/.test(packs[0] || '') ? packs.join(' + ') : '';
        const scent = p === 'laundry' && lines[0]?.variant.scent ? lines[0].variant.scent : '';
        const detail = [`${washes.toLocaleString(LOCALE)} washes`, scent, breakdown, roll].filter(Boolean).join(' · ');
        return row(name, detail);
      });
      // Paid hardware extras, up to the cap, never beside a free set, and only while the hardware
      // carries a live price (hwMax is 0 otherwise). The tick only appears once a set is actually
      // added, so a zero-quantity stepper reads as an offer, not as contents.
      const g = this.hardware || {};
      const setRaw = (Number(g.bottlePrice) || 0) + (Number(g.clothPrice) || 0);
      if (g.bottleVariantId && g.clothVariantId && !mscHw && setRaw > 0 && (hwMax > 0 || this.hw > 0)) {
        const note = (C.hwNote || '[price] a set · up to [max] a bundle')
          .replace('[price]', money(setRaw)).replace('[max]', hwCap);
        const stepper = `<span class="dip-recap-qty"><button type="button" class="dip-recap-step" data-step="hw" data-dir="-1" aria-label="Fewer">&minus;</button><span class="dip-recap-qtyn">${this.hw}</span><button type="button" class="dip-recap-step" data-step="hw" data-dir="1" aria-label="More">+</button></span>`;
        rows.push(`<li><span class="dip-recap-tx"><span class="dip-recap-t">${this.hw > 0 ? this.CHECK : ''}${C.hwTitle || 'Extra spray bottles & cloths'}</span><span class="dip-recap-s">${note}</span></span>${stepper}</li>`);
      }
      this.refs.recapList.innerHTML = rows.join('');
    }

    // ---- savings stack: the full decomposition of the pack-by-pack story (Charlie
    // 2026-08-26). Bulk pack sizes (smallest-pack route vs these packs, pre-bonus) +
    // the bundle bonus + the kit. The sub-vs-one-time gap is still never a "saving": it
    // shows as plain prices on the purchase-type rows.
    const bulkSaving = Math.max(0, Math.round(small.cost - sheetsGross));
    const total = bulkSaving + T.bundleSaving;
    const set = (rowRef, valRef, amount) => {
      if (this.refs[rowRef]) this.refs[rowRef].hidden = !(amount > 0);
      if (this.refs[valRef] && amount > 0) this.refs[valRef].textContent = `−${money(amount)}`;
    };
    set('svBulkRow', 'svBulk', bulkSaving);
    set('svBundleRow', 'svBundle', T.bundleSaving);
    if (this.refs.svBundleNote) {
      const parts = [];
      if (bonus.breadth > 0 && bonus.pct > 0) parts.push(`${bonus.breadth}% for bundling`);
      if (bonus.depth > 0 && bonus.pct > 0) parts.push(`${bonus.depth}% for volume`);
      this.refs.svBundleNote.textContent = parts.join(' + ');
    }
    if (this.refs.svTotal) this.refs.svTotal.textContent = money(total);
    if (this.refs.savings) this.refs.savings.hidden = !(total > 0);

    // ---- price block
    if (this.refs.was) {
      // The struck price = the same goods bought as individual packs on the SAME plan,
      // i.e. the pre-bonus total for the active purchase type. Struck only when the bonus
      // genuinely lowers it.
      const anchor = this.sub ? T.gross : T.once;
      const show = anchor > T.net;
      this.refs.was.hidden = !show;
      this.refs.was.textContent = show ? money(anchor) : '';
    }
    if (this.refs.price) this.refs.price.textContent = money(T.net);
    if (this.refs.billed) {
      // Per-month equivalent follows the MONEY: the billing cadence on subscription, the
      // longest coverage in the box on one-time.
      const covs = selected.map((p) => this.#coverageOf(p)).filter(Boolean);
      const divisor = this.sub && this.months ? this.months : (covs.length ? Math.max(...covs) : 0);
      const perMo = divisor > 0 ? `, about ${money(T.net / divisor)} a month` : '';
      // Delivery judged on the order total (ruling 4): the sub-threshold rate or the free word.
      const ship = renewalShipMinor(T.net, this.offer);
      const shipTxt = ` + ${ship ? money(ship) : (this.offer?.labels?.free || 'free')} delivery`;
      // Charlie 2026-09-08: one sentence with the figure ("£127.30 every 3 months + free delivery, about £42.43 a
      // month"), the figure itself already printed in the bold slot.
      this.refs.billed.textContent = this.sub && this.months
        ? ` ${this.#cadenceWord(this.months)}${shipTxt}${perMo}`
        : `, one-time${shipTxt}${perMo}`;
    }
    if (this.refs.save) {
      this.refs.save.textContent = total > 0 ? `${C.saveWord || 'Save'} ${money(total)} today` : '';
    }

    // ---- honesty notes
    if (this.refs.renewNote) {
      let note = '';
      if (this.sub && T.pct > 0) note = C.renewFull || '';
      else if (bonus.wanted > 0 && !bonus.code) note = C.noCodeNote || '';
      this.refs.renewNote.hidden = !note;
      this.refs.renewNote.textContent = note;
    }

    // ---- one-time in one tap
    if (this.refs.once) {
      this.refs.once.hidden = !this.sub;
      this.refs.once.textContent = `${C.onceTitle || 'One-time purchase'} · ${money(onceT.net)}`;
    }

    // ---- empty state: nothing selected reads as an invitation, not a broken panel
    const empty = selected.length === 0;
    if (this.refs.totalBlock) this.refs.totalBlock.hidden = empty;
    if (this.refs.recap) this.refs.recap.hidden = empty;
    if (empty) {
      if (this.refs.save) this.refs.save.textContent = '';
      if (this.refs.renewNote) this.refs.renewNote.hidden = true;
      if (this.refs.once) this.refs.once.hidden = true;
      if (this.refs.subPrice) this.refs.subPrice.textContent = '';
      if (this.refs.oncePrice) this.refs.oncePrice.textContent = '';
    }

    // ---- CTA
    if (this.refs.ctaLabel) {
      const savePill = total > 0 ? ` <span class="dip-cta-save">${C.saveWord || 'Save'} ${money(total)}</span>` : '';
      this.refs.ctaLabel.innerHTML = empty
        ? (C.emptyNote || 'Pick a product to get started')
        : (T.net > 0 ? `${C.ctaWord || 'Add to basket'} · ${money(T.net)}${savePill}` : (C.ctaWord || 'Add to basket'));
    }
    if (this.refs.cta) this.refs.cta.disabled = !(T.net > 0 && T.lines.length > 0);
  }

  // ---- Cart -------------------------------------------------------------------------
  #lineItems() {
    // `_dip_src` (hidden line property): the surface the add came from, for attribution. See dip-simple-buy.js.
    const items = this.#basketLines().map(({ variant, qty }) => {
      const line = { id: Number(variant.id), quantity: qty, properties: { _dip_src: 'bundle-save' } };
      if (this.sub && variant.plans && variant.plans[this.months]) {
        line.selling_plan = Number(variant.plans[this.months].id);
      }
      return line;
    });
    // The multi-surface rung's hardware (rulings 3 + 9): flagged `_hw` so the shared dedupe
    // never doubles a set already in the basket; zero-priced included listings since fix (b).
    const mscHw = this.#mscHardware();
    if (mscHw && items.length) items.push(...mscHw.items);
    // Paid hardware extras: plain lines at the live price, never flagged (they are bought, not
    // gifted, so the dedupe must not drop them). Only ever posted without a free set in play and
    // only while the hardware carries a price (render() clamps hw to 0 otherwise; guarded again).
    const g = this.hardware || {};
    const gSetRaw = (Number(g.bottlePrice) || 0) + (Number(g.clothPrice) || 0);
    if (!mscHw && gSetRaw > 0 && this.hw > 0 && items.length) {
      if (g.bottleVariantId) items.push({ id: Number(g.bottleVariantId), quantity: this.hw });
      if (g.clothVariantId) items.push({ id: Number(g.clothVariantId), quantity: this.hw });
    }
    return items;
  }

  async #addToCart() {
    const cta = this.refs.cta;
    if (this.refs.error) this.refs.error.hidden = true;
    const bonus = this.#bonus();
    const items = await reconcileHardwareLines(this.#lineItems(), this.hardware, this.model.msc?.variants || []);
    if (!items.length) return;
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);

    cta?.setAttribute('aria-busy', 'true');
    this.adding = true;
    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('javascript'),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await res.json();
      if (data.status) {
        this.#showError(data.description || data.message || 'Could not add to basket.');
        this.dispatchEvent(new CartErrorEvent('dip-bundle-save', data.message, data.description, data.errors));
        return;
      }
      tagCart(); // the running test's arm on the cart, as every buy surface does (assets/dip-arm.js)
      // The bonus code lands AFTER the lines exist. Failure is not fatal to the add: the
      // basket is right, and the drawer shows the real total rather than a broken promise.
      if (bonus.code) await this.#applyCode(bonus.code);

      const itemCount = dipCartCountFromSections(data.sections);
      this.#announce('Bundle added to your basket');
      const label = this.refs.ctaLabel || cta;
      if (label) {
        const prev = label.innerHTML;
        label.textContent = '✓ Added to basket';
        setTimeout(() => { this.adding = false; label.innerHTML = prev; this.render(); }, 1600);
      }
      this.dispatchEvent(new CartAddEvent(undefined, 'dip-bundle-save', {
        source: 'dip-bundle-save', itemCount: itemCount ?? items.length, sections: data.sections,
      }));
    } catch (err) {
      console.error('[dip-bundle-save] add to cart failed', err);
      this.#showError('Something went wrong. Please try again.');
      this.adding = false;
    } finally {
      cta?.removeAttribute('aria-busy');
    }
  }

  /** Supported cart update API (dip-msc-guard.js precedent), never the /discount/ cookie. */
  async #applyCode(code) {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: code }),
      });
    } catch (err) {
      console.error('[dip-bundle-save] could not apply bundle code', err);
    }
  }

  #showError(msg) { if (this.refs.error) { this.refs.error.textContent = msg; this.refs.error.hidden = false; } }
  #announce(msg) { if (this.refs.live) this.refs.live.textContent = msg; }
}

if (!customElements.get('dip-bundle-save')) customElements.define('dip-bundle-save', DipBundleSave);
